import PropTypes from "prop-types";
import { H2, Paragraph } from "../../../styles/AppStyles";
import ContentLayout from "../../../layout/ContentLayout";
import { LINKS, href } from "../../../config/links";

const ShadowIT = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.THREAT_TYPE.SHADOW_IT);
  const seoTitle =
    "What Is Shadow IT? | How Unauthorized Tools Create Security Blind Spots";
  const description =
    "Shadow IT — unauthorized apps, cloud services, and AI tools employees use without IT approval — removes your visibility and control over sensitive data. Learn how Shadow IT enables breaches, how it intersects with insider threats and data exfiltration, and how to build it into your security program.";

  const displayTitle = "What Is Shadow IT?";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: displayTitle,
    description,
    mainEntityOfPage: canonicalUrl,
    publisher: { "@type": "Organization", name: "CSFSafe" },
  };

  return (
    <ContentLayout
      istouch={istouch}
      isdarkmode={isdarkmode}
      displayTitle={displayTitle}
      seoTitle={seoTitle}
      canonicalUrl={canonicalUrl}
      description={description}
      ogTitle="Shadow IT Explained — CSFSafe Cybersecurity Learning Center"
      ogDescription="Unauthorized apps, cloud tools, and AI services create hidden attack surfaces your security controls can't reach. Learn how Shadow IT becomes a security risk and how to integrate it into your cybersecurity program."
      jsonLd={jsonLd}
      // heroImageSrc="/images/articles/shadow-it.png"
      heroImageAlt="Illustration of Shadow IT — unauthorized apps operating outside IT visibility"
      lastUpdatedLabel="Last updated: April 27, 2026"
    >
      <span id="what-is-shadow-it" />

      <Paragraph>
        <strong>Shadow IT</strong> is any software, cloud service, hardware, or
        AI tool that employees or teams adopt for work purposes without the
        knowledge or approval of the IT or security team. It is not malware —
        it is legitimate technology used by authorized people in unauthorized
        ways, completely outside the organization's security controls.
      </Paragraph>

      <Paragraph>
        The threat Shadow IT poses is not the tool itself. The threat is{" "}
        <strong>loss of visibility</strong>: your data governance policies,
        encryption standards, access controls, logging, and breach detection
        all stop at the edge of systems IT knows about. Shadow IT creates a
        parallel environment where sensitive data flows freely, unmonitored and
        unprotected, beyond the reach of every security control your
        organization has built.
      </Paragraph>

      <Paragraph>
        For{" "}
        <strong>small businesses</strong>,
        the risk is compounded by limited IT resources and the absence of
        dedicated security teams. When a staff member signs up for a personal
        Dropbox account to share files or pastes customer data into an AI
        chatbot to speed up a task, there is often nobody positioned to
        notice — until a breach makes it visible.
      </Paragraph>

      <H2>How Shadow IT Becomes a Security Risk</H2>

      <Paragraph>
        Shadow IT creates several overlapping attack surfaces that are
        fundamentally different from externally-sourced threats:
      </Paragraph>

      <ul>
        <li>
          <strong>Unvetted third-party vendors.</strong> Every Shadow IT tool is
          an implicit third-party relationship with no contract, no data
          processing agreement, and no breach notification obligation. If the
          vendor is compromised, your data goes with it — and you may not learn
          about it for months. This is the same risk that drives{" "}
          <a href={LINKS.THREAT_TYPE.SUPPLY_CHAIN}>supply chain attacks</a>,
          except the relationship was never formally established.
        </li>
        <li>
          <strong>Weak credentials and no MFA enforcement.</strong> Personal
          accounts created by employees for work use are outside your identity
          governance. Employees often reuse passwords and skip multi-factor
          authentication on personal accounts. A{" "}
          <a href={LINKS.THREAT_TYPE.CREDENTIAL_STUFFING}>
            credential stuffing
          </a>{" "}
          attack against a breached password database can silently open access
          to those accounts and the work data stored in them.
        </li>
        <li>
          <strong>Unmanaged data exfiltration paths.</strong> Shadow IT is one
          of the most common mechanisms behind{" "}
          <a href={LINKS.THREAT_TYPE.DATA_EXFILTRATION}>data exfiltration</a>.
          Employees moving files into personal cloud storage, pasting sensitive
          data into AI tools, or syncing work content to personal devices are
          all moving organizational data outside your control — even when there
          is no malicious intent. Research indicates shadow data is involved in
          one in three breaches.
        </li>
        <li>
          <strong>Invisible attack surface.</strong> Unmanaged applications are
          rarely kept up to date. An old browser extension, an unmaintained
          SaaS client, or a forgotten cloud tenancy running on a personal credit
          card can carry exploitable{" "}
          <a href={LINKS.THREAT_TYPE.VULNERABILITY_EXPLOITATION}>
            unpatched vulnerabilities
          </a>{" "}
          that IT never knew needed attention.
        </li>
        <li>
          <strong>Cloud misconfiguration amplified.</strong> Employees who spin
          up personal cloud accounts for work rarely apply the same
          configuration standards IT would require. Storage buckets left public,
          API keys committed to unmanaged repositories, and services exposed
          without authentication are classic{" "}
          <a href={LINKS.THREAT_TYPE.CLOUD_MISCONFIGURATION}>
            cloud misconfiguration
          </a>{" "}
          scenarios — except the account is unknown to IT, so the misconfiguration
          is never caught.
        </li>
        <li>
          <strong>Incomplete offboarding.</strong> When employees leave, IT can
          revoke access to every managed system. Shadow IT accounts exist
          outside that process. Research shows 31% of former employees retain
          access to former employers' SaaS tools after departure — a persistent
          form of{" "}
          <a href={LINKS.THREAT_TYPE.ACCOUNT_TAKEOVER}>account-level risk</a>{" "}
          that often goes unnoticed until a departing employee accesses data
          they should no longer reach.
        </li>
      </ul>

      <H2>Shadow AI: The Fastest-Growing Subset</H2>

      <Paragraph>
        Generative AI has created a new and rapidly expanding category of Shadow
        IT. Employees across every industry are adopting AI tools — for writing,
        summarizing, coding, data analysis, and research — often without
        organizational approval or any awareness of what happens to the inputs
        they provide.
      </Paragraph>

      <Paragraph>
        The Samsung case is the defining example: within a single month of
        engineers being permitted to use ChatGPT, three separate incidents
        occurred in which proprietary source code, internal meeting transcripts,
        and chip test data were pasted directly into the tool. Because the AI
        provider used conversation data to improve its models at the time,
        Samsung's intellectual property was potentially incorporated into a
        third-party system with no way to retrieve or delete it.
      </Paragraph>

      <ul>
        <li>
          Shadow AI tool usage grew <strong>156%</strong> from 2023 to 2025.
        </li>
        <li>
          <strong>20%</strong> of organizations have experienced a security
          breach directly attributable to Shadow AI.
        </li>
        <li>
          Shadow AI breaches add an average of <strong>$670,000</strong> in
          additional cost above a standard data breach.
        </li>
        <li>
          <strong>70%</strong> of employees who use ChatGPT at work actively
          conceal that usage from their employer — meaning bans rarely work
          without a credible approved alternative.
        </li>
      </ul>

      <H2>Relationship to Other Threat Types</H2>

      <Paragraph>
        Shadow IT rarely causes incidents on its own. Its role is to remove the
        controls that would otherwise catch or contain other threats:
      </Paragraph>

      <ul>
        <li>
          It enables{" "}
          <a href={LINKS.THREAT_TYPE.INSIDER_THREATS}>insider threats</a> by
          creating unmonitored data paths that careless or malicious users can
          exploit.
        </li>
        <li>
          It expands the scope of{" "}
          <a href={LINKS.THREAT_TYPE.DATA_EXFILTRATION}>data exfiltration</a>{" "}
          attacks by providing attacker-controlled (or simply ungoverned)
          staging locations for stolen data.
        </li>
        <li>
          It amplifies{" "}
          <a href={LINKS.THREAT_TYPE.CLOUD_MISCONFIGURATION}>
            cloud misconfiguration
          </a>{" "}
          risk by creating cloud resources that will never appear in a security
          review.
        </li>
        <li>
          It widens the{" "}
          <a href={LINKS.THREAT_TYPE.SUPPLY_CHAIN}>supply chain</a> attack
          surface by adding unvetted vendors to the organization's effective
          trust boundary.
        </li>
        <li>
          It creates residual{" "}
          <a href={LINKS.THREAT_TYPE.ACCOUNT_TAKEOVER}>account takeover</a>{" "}
          risk through unmanaged credentials and incomplete offboarding.
        </li>
      </ul>

      <H2>Integrating Shadow IT Into Your Security Program</H2>

      <Paragraph>
        Shadow IT is a governance problem before it is a technology problem.
        Organizations that manage it effectively do so by weaving it into the
        same security framework they apply to every other risk.
      </Paragraph>

      <ul>
        <li>
          <strong>Asset inventory.</strong> Your{" "}
          <a
            href={LINKS.CYBERSECURITY_FRAMEWORK.ASSET_INVENTORY_CLASSIFICATION}
          >
            asset inventory
          </a>{" "}
          should explicitly include a Shadow IT discovery process. DNS/firewall
          logs, expense reports, and periodic employee surveys are all practical
          inputs for a small organization without enterprise SaaS management
          tooling.
        </li>
        <li>
          <strong>Risk identification.</strong> Shadow IT — and Shadow AI
          specifically — should appear as named risk scenarios in your{" "}
          <a href={LINKS.CYBERSECURITY_FRAMEWORK.RISK_IDENTIFICATION_ANALYSIS}>
            risk identification and analysis
          </a>{" "}
          process, with documented likelihood and impact assessments.
        </li>
        <li>
          <strong>Policies and procedures.</strong> An acceptable use policy
          that defines Shadow IT, gives concrete examples, and explains
          consequences — alongside a fast, simple app approval process — is the
          foundational control. See{" "}
          <a href={LINKS.CYBERSECURITY_FRAMEWORK.POLICIES_AND_PROCEDURES}>
            policies and procedures
          </a>{" "}
          for guidance on building this into your program.
        </li>
        <li>
          <strong>Access control.</strong> Requiring single sign-on (SSO) and
          MFA for all work-related tools — including those that started as
          personal accounts — brings Shadow IT back into your identity governance.
          See{" "}
          <a href={LINKS.BEST_PRACTICES.ACCESS_CONTROL}>
            access control and password best practices
          </a>{" "}
          for practical guidance.
        </li>
        <li>
          <strong>Quarterly audits.</strong> Shadow IT audits should be a
          standing item in{" "}
          <a href={LINKS.CYBERSECURITY_FRAMEWORK.QUARTERLY_AUDITS}>
            quarterly reviews
          </a>
          : check DNS/firewall logs for new cloud services, review expense
          reports for SaaS subscriptions, and ask department heads what tools
          their teams have adopted.
        </li>
        <li>
          <strong>Monthly training.</strong> Include Shadow IT awareness in{" "}
          <a href={LINKS.CYBERSECURITY_FRAMEWORK.MONTHLY_TRAINING}>
            monthly security training
          </a>
          . Employees are far less likely to use unauthorized tools when they
          understand why it matters and have a fast, approved path to request
          new ones.
        </li>
        <li>
          <strong>Threat landscape monitoring.</strong> The pace of new AI tool
          adoption makes Shadow IT a rapidly shifting risk. Include it in your{" "}
          <a href={LINKS.CYBERSECURITY_FRAMEWORK.THREAT_LANDSCAPE_MONITORING}>
            threat landscape monitoring
          </a>{" "}
          cadence so your program stays current with what employees are actually
          using.
        </li>
      </ul>

      <H2>The CSFSafe Cybersecurity Playbook</H2>

      <Paragraph>
        The <strong>CSFSafe Cybersecurity Playbook</strong> gives small
        businesses the policies, checklists,
        and risk management templates to address Shadow IT as part of a
        complete, right-sized security program:
      </Paragraph>

      <ul>
        <li>
          Acceptable use and data handling policies that explicitly address
          Shadow IT and AI tool usage
        </li>
        <li>
          Asset inventory and risk register worksheets that include Shadow IT
          discovery
        </li>
        <li>
          Quarterly audit checklists covering unauthorized applications and
          cloud services
        </li>
        <li>
          Business continuity and incident response templates for Shadow IT
          breach scenarios
        </li>
      </ul>

      <Paragraph>
        Explore the full toolkit at{" "}
        <a href={LINKS.MARKETING.PLAYBOOK}>the CSFSafe Cybersecurity Playbook</a>{" "}
        and browse related topics in the{" "}
        <a href={LINKS.LEARNING_CENTER}>Cybersecurity Learning Center</a>.
      </Paragraph>

      <H2>Practical Prevention Guide</H2>

      <Paragraph>
        For a step-by-step guide to discovering, assessing, and managing Shadow
        IT in your organization — including how to build a fast approval process
        and handle generative AI specifically — see the full CSFSafe article:{" "}
        <a href={LINKS.ARTICLE.SHADOW_IT}>
          Shadow IT: What It Is, Why It Happens, and How to Get It Under Control
        </a>
        .
      </Paragraph>

      <H2>Further Reading</H2>

      <ul>
        <li>
          <a
            href="https://www.ibm.com/think/topics/shadow-it"
            target="_blank"
            rel="noreferrer"
          >
            What Is Shadow IT? (IBM)
          </a>
        </li>
        <li>
          <a
            href="https://www.cisco.com/site/us/en/learn/topics/security/what-is-shadow-it.html"
            target="_blank"
            rel="noreferrer"
          >
            What Is Shadow IT? (Cisco)
          </a>
        </li>
        <li>
          <a
            href="https://www.crowdstrike.com/en-us/cybersecurity-101/cloud-security/shadow-it/"
            target="_blank"
            rel="noreferrer"
          >
            Shadow IT: Risks &amp; Benefits (CrowdStrike)
          </a>
        </li>
        <li>
          <a
            href="https://www.cloudflare.com/learning/access-management/what-is-shadow-it/"
            target="_blank"
            rel="noreferrer"
          >
            What Is Shadow IT? (Cloudflare)
          </a>
        </li>
      </ul>
    </ContentLayout>
  );
};

ShadowIT.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default ShadowIT;
